import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { PrimaryButton } from '../../components/PrimaryButton';
import LoadingLogo from '../../components/LoadingLogo';
import GenericCard from '../Users/GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const BrandMake = () => {
  const history = useHistory();
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [currentPageBrands, setCurrentPageBrands] = useState([]);
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({ needle: '' }); 
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showModule, setShowModule] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [
    { name: 'needle', type: 'search' },
  ];

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const updatePagination = useCallback((data, page, size) => {
    setTotalPages(Math.ceil((data?.length || 0) / size) || 1);
    setCurrentPageBrands(paginateData(data || [], page, size));
  }, []);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureGet('/vehicle-brands', showSnackbar, { signal });
      const list = res?.data || [];
      setBrands(list);
      setFilteredBrands(list);
      setCurrentPage(0);
      updatePagination(list, 0, pageSize);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching brands:', err);
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize, updatePagination]);

  useEffect(() => {
    const ac = new AbortController();
    fetchBrands();
    return () => ac.abort();
  }, [fetchBrands]);

  const applyFilters = useCallback(() => {
    let filtered = [...brands];

    if (filters.needle) {
      const q = filters.needle.toLowerCase();
      filtered = filtered.filter((b) =>
        (b.name || '').toLowerCase().includes(q)
      );
    }

    setFilteredBrands(filtered);
    setCurrentPage(0);
    updatePagination(filtered, 0, pageSize);
  }, [brands, filters, pageSize, updatePagination]);

  useEffect(() => {
    applyFilters();
  }, [filters, brands, applyFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageBrands(paginateData(filteredBrands, page, pageSize));
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    updatePagination(filteredBrands, 0, newPageSize);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ needle: '' });
    setFilteredBrands(brands);
    updatePagination(brands, 0, pageSize);
  };

  const handleAddClick = () => {
    setSelectedBrand(null);
    setShowModule(true);
  };

  const handleEditClick = (brand) => {
    setSelectedBrand(brand);
    setShowModule(true);
  };

  const handleDeleteClick = (brand) => {
    setItemToDelete(brand);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/vehicle-brands/${itemToDelete.id}`, showSnackbar);
      await fetchBrands();
      showSnackbar(t('confirmation.brandDelete') || 'Marque supprimée', 'success');
    } catch (e) {
      console.error('Error deleting brand:', e);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') fetchBrands();
    setShowModule(false);
    setSelectedBrand(null);
  };

  const getMenuOptions = (brand) => ([
    { label: t('actions.modify'), icon: EditIcon, action: () => handleEditClick(brand) },
    { label: t('actions.delete'), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(brand) },
  ]);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t('titles.vehicleBrands')}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t('titles.vehicleBrands') }</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t('primaryButtons.add_brandMake')}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="relative mt-6">
        {loading && (
          <div className="absolute top-0 left-0 w-full flex justify-center z-10">
            <LoadingLogo />
          </div>
        )}

        {/* Brand Cards */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && currentPageBrands.map((brand) => (
            <div
              key={brand.id}
              className="cursor-pointer"
              onClick={() => history.push('/vehicle-models', { vehicleBrandId: brand.id })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && history.push('/vehicle-models', { vehicleBrandId: brand.id })}
            >
              <GenericCard
                title={brand.name || ''}
                subtitle={brand.code || ''}
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>
                    <OptionsMenu options={getMenuOptions(brand)} />
                  </div>
                }
              />
            </div>
          ))}
        </div>

        {!loading && currentPageBrands.length === 0 && (
          <div className="text-center mt-4">
            <p>{t('placeholder.noResults')}</p>
          </div>
        )}
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="brandMake"             
          mode={selectedBrand ? 'modify' : 'add'}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedBrand || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default BrandMake;
